import type { ZodType } from 'zod'
import * as z from 'zod'

type ZodFileCheck
  = | { kind: 'minDimensions', width: number, height: number }
    | { kind: 'maxDimensions', width: number, height: number }
    | { kind: 'minSize', size: number }
    | { kind: 'maxSize', size: number }
    | { kind: 'image' }
    | { kind: 'video' }

interface FileValue {
  id: number
  path: string
  filename: string
  originalFilename: string
  contentType: string
  public: boolean
  uploadedFile?: File
}

function checkDimensions(
  type: 'min' | 'max',
  allowedWidth: number,
  allowHeight: number,
  realWidth: number,
  realHeight: number,
) {
  if (type === 'min') {
    if (allowedWidth > realWidth || allowHeight > realHeight) {
      return false
    }
    return true
  }
  if (type === 'max') {
    if (allowedWidth < realWidth || allowHeight < realHeight) {
      return false
    }
    return true
  }
  return true
}

type ZodFileSchema = ZodType<FileValue> & {
  minSize: (size: number) => ZodFileSchema
  maxSize: (size: number) => ZodFileSchema
  image: () => ZodFileSchema
  video: () => ZodFileSchema
  minDimensions: (width: number, height: number) => ZodFileSchema
  maxDimensions: (width: number, height: number) => ZodFileSchema
}

type RefinementCtx<T> = Parameters<Parameters<ZodType<T>['superRefine']>[0]>[1]

function addCustomIssue(ctx: RefinementCtx<FileValue>, issue: Record<string, unknown>) {
  ctx.addIssue({
    code: 'custom',
    params: {
      issue,
    },
  })
}

function createZodFile(checks: ZodFileCheck[]): ZodFileSchema {
  const schema = z.custom<FileValue>(() => true).superRefine(async (data, ctx) => {
    const value = data as undefined | null | FileValue

    const hasNewFile = !!(value?.uploadedFile)
    const hasOldFile = !!(
      value?.id && value?.filename && value?.path
    )
    if (!hasNewFile && !hasOldFile) {
      addCustomIssue(ctx, {
        code: 'required',
      })
    }

    if (hasNewFile) {
      let contentType = 'any'
      if (value?.uploadedFile?.type.startsWith('image/')) {
        contentType = 'image'
      }
      else if (value?.uploadedFile?.type.startsWith('video/')) {
        contentType = 'video'
      }
      for (const check of checks) {
        if (check.kind === 'image') {
          if (contentType !== 'image') {
            addCustomIssue(ctx, {
              code: 'invalid_file_image',
            })
          }
        }
        else if (check.kind === 'video') {
          if (contentType !== 'video') {
            addCustomIssue(ctx, {
              code: 'invalid_file_video',
            })
          }
        }
        else if (check.kind === 'minDimensions' || check.kind === 'maxDimensions') {
          const isMax = check.kind === 'maxDimensions'
          if (contentType === 'image') {
            const passed: boolean = await new Promise((resolve) => {
              const image = new Image()
              const objectUrl = URL.createObjectURL(value!.uploadedFile!)
              image.onload = function () {
                const passed = checkDimensions(
                  isMax ? 'max' : 'min',
                  check.width,
                  check.height,
                  image.naturalWidth,
                  image.naturalHeight,
                )
                URL.revokeObjectURL(objectUrl)
                resolve(passed)
              }
              image.onerror = function () {
                URL.revokeObjectURL(objectUrl)
                resolve(false)
              }
              image.src = objectUrl
            })
            if (!passed) {
              addCustomIssue(ctx, {
                code: 'invalid_file_image_dimensions',
                type: isMax ? 'max' : 'min',
                width: check.width,
                height: check.height,
              })
            }
          }
        }
        else if (check.kind === 'minSize') {
          if (check.size > value!.uploadedFile!.size) {
            addCustomIssue(ctx, {
              code: 'invalid_file_size',
              type: 'min',
              size: check.size,
            })
          }
        }
        else if (check.kind === 'maxSize') {
          if (check.size < value!.uploadedFile!.size) {
            addCustomIssue(ctx, {
              code: 'invalid_file_size',
              type: 'max',
              size: check.size,
            })
          }
        }
      }
    }
  })

  return Object.assign(schema, {
    minSize: (size: number) => createZodFile([...checks, { kind: 'minSize', size }]),
    maxSize: (size: number) => createZodFile([...checks, { kind: 'maxSize', size }]),
    image: () => createZodFile([...checks, { kind: 'image' }]),
    video: () => createZodFile([...checks, { kind: 'video' }]),
    minDimensions: (width: number, height: number) => createZodFile([
      ...checks,
      { kind: 'minDimensions', width, height },
    ]),
    maxDimensions: (width: number, height: number) => createZodFile([
      ...checks,
      { kind: 'maxDimensions', width, height },
    ]),
  }) as ZodFileSchema
}

class ZodFile {
  static create() {
    return createZodFile([])
  }
}

declare module 'zod' {
  export function file(): ZodFileSchema
}

export { ZodFile }
